import type { Currency } from '../../constants';

// Point earning (Redeem, Earn, etc.)
export interface EarnPointsRequest {
  customerId: string;
  points: number;
  activityType: string;
  pointType: string;
  orderId?: string;
  campaignId?: string;
  expiryDays?: number;
}

export interface PointTransactionResponse {
  customerId: string;
  transactionId: string;
  timestamp: string;
  pointsDelta: number;
  remainingPoints: number;
  activityType: string;
  pointType: string;
  earnDate: string;
  expireAt: string;
  creditDelta: number;
  remainingCredit: number;
  orderId?: string;
  rewardId?: string;
  campaignId?: string;
}

// Point redemption / Reward exchange
export interface RedeemPointsRequest {
  customerId: string;
  points: number;
  rewardId: string;
}

export interface RedeemPointsResponse {
  success: boolean;
  redeemedPoints: number;
}

export interface NextExpiringCredit {
  credit: number;
  expireAt: string;
}

// User point balance and tier
export interface CustomerBalanceResponse {
  customerId: string;
  availablePoints: number;
  earned: number;
  redeemed: number;
  tier: string;
  userRegistered?: string;
  /** Display currency code */
  currency: Currency;
  /** Fraction digits for display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  /** Available credit in display currency (converted from availablePoints) */
  availableCredit: number;
  /** Total credit earned in display currency (lifetime) */
  earnedCredit: number;
  /** Total credit redeemed in display currency (lifetime) */
  redeemedCredit: number;
  nextExpiring: NextExpiringCredit | null;
}

// Transaction history
export interface TransactionHistoryResponse {
  transactions: PointTransactionResponse[];
  /** Display currency code */
  currency: Currency;
  /** Fraction digits for display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  /** Base64-encoded pagination cursor */
  lastEvaluatedKey?: string;
}

// Tier update request/response
export interface UpdateTierRequest {
  customerId: string;
  newTier: string;
}

export interface UpdateCustomerTierResponse {
  customerId: string;
  tier: string;
}

// Reward creation/retrieval
export interface CreateRewardRequest {
  name: string;
  description: string;
  pointCost: number;
  status: string;
}

export interface RewardResponse {
  rewardId: string;
  name: string;
  description: string;
  pointCost: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
}

// Tier definition
export interface CreateTierDefinitionRequest {
  tier: string;
  rank: number;
  minPoints: number;
  benefits: string[];
}

export interface TierDefinitionResponse {
  tier: string;
  rank: number;
  minPoints: number;
  benefits: string[];
  createdAt: string;
  updatedAt?: string;
}

// Tier point information for earning calculation
export interface TierPointInfo {
  tier: string;
  pointMultiplier: number;
  pointsPerCurrency: number;
}

// Loyalty policy configuration
export interface LoyaltyPolicyResponse {
  currencyCode: string;
  currencyPerPoint: number;
  maxWeeklyInteractionPoint: number;
  currentWeeklyInteractionPoint: number;
  pointValueInCurrency: number;
  /** Fraction digits for the display currency (0 for JPY/CLP, 2 for others) */
  fractionDigits: number;
  maxApplicablePointsPercent: number;
  tierPointInfo?: TierPointInfo[];
  interactionPoints: {
    Browsing: number;
  };
}
